import { useState } from "react"
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { type Position } from "@/types/portfolio"
import { useCurrency } from "@/context/currency/currency-context.ts"

const MAX_SLICES = 10

const COLORS = [
	"#0ea5e9", // sky-500
	"#38bdf8", // sky-400
	"#0c4a6e", // sky-900
	"#7dd3fc", // sky-300
	"#075985", // sky-800
	"#082f49", // sky-950
	"#0369a1", // sky-700
	"#bae6fd", // sky-200
	"#0284c7", // sky-600
	"#e0f2fe", // sky-100
]

const OTHERS_COLOR = "#94a3b8" // slate-400

interface ChartData {
	name: string
	value: number
	companyName: string
	[key: string]: string | number
}

interface PortfolioPieChartProps {
	positions: Position[]
}

export function PortfolioPieChart({ positions }: PortfolioPieChartProps) {
	const { format, convert } = useCurrency()
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

	const allData: ChartData[] = positions
		.map((pos) => ({
			name: pos.ticker,
			value: convert((pos.shares || 0) * (pos.price || 0), pos.currency),
			companyName: pos.name || pos.ticker,
		}))
		.sort((a, b) => b.value - a.value)

	const data: ChartData[] =
		allData.length > MAX_SLICES
			? [
					...allData.slice(0, MAX_SLICES),
					{
						name: "Others",
						value: allData.slice(MAX_SLICES).reduce((sum, d) => sum + d.value, 0),
						companyName: `${allData.length - MAX_SLICES} more positions`,
					},
				]
			: allData

	const totalValue = data.reduce((sum, item) => sum + item.value, 0)
	const activeItem = selectedIndex !== null ? data[selectedIndex] : null

	if (positions.length === 0) {
		return (
			<Card className="h-full bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10 flex items-center justify-center min-h-[200px] md:min-h-[300px]">
				<p className="text-muted-foreground">No nectar collected yet.</p>
			</Card>
		)
	}

	return (
		<Card className="h-full bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
			<CardHeader>
				<CardTitle className="text-sky-950 dark:text-sky-50">Hive Allocation</CardTitle>
			</CardHeader>
			<CardContent>
				{/* Chart with center label */}
				<div className="h-[200px] md:h-[260px] w-full relative" onClick={() => setSelectedIndex(null)}>
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={data}
								cx="50%"
								cy="50%"
								innerRadius={55}
								outerRadius={75}
								paddingAngle={5}
								dataKey="value"
								isAnimationActive={true}
								onClick={(_, index, e) => {
									e.stopPropagation()
									setSelectedIndex((prev) => (prev === index ? null : index))
								}}
								style={{ cursor: "pointer", outline: "none" }}
							>
								{data.map((entry, index) => (
									<Cell
										key={`cell-${index}`}
										fill={entry.name === "Others" ? OTHERS_COLOR : COLORS[index % COLORS.length]}
										opacity={selectedIndex === null || selectedIndex === index ? 1 : 0.35}
										stroke="none"
									/>
								))}
							</Pie>
						</PieChart>
					</ResponsiveContainer>

					{/* Center donut label */}
					<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
						<div className="text-center px-2">
							{activeItem ? (
								<>
									<p className="text-[11px] font-bold text-sky-900 dark:text-sky-100 leading-tight max-w-[100px] truncate">
										{activeItem.name === "Others"
											? (activeItem.companyName as string)
											: activeItem.name}
									</p>
									<p className="text-sm font-semibold">
										{format(activeItem.value, { skipConversion: true })}
									</p>
									<p className="text-[10px] text-muted-foreground">
										{((activeItem.value / (totalValue || 1)) * 100).toFixed(1)}%
									</p>
								</>
							) : (
								<>
									<p className="text-[10px] text-muted-foreground">Total</p>
									<p className="text-sm font-semibold">
										{format(totalValue, { skipConversion: true })}
									</p>
								</>
							)}
						</div>
					</div>
				</div>

				{/* Custom legend — wraps freely, never cut off */}
				<div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-3">
					{data.map((entry, index) => (
						<div
							key={entry.name}
							className="flex items-center gap-1.5 cursor-pointer"
							onClick={(e) => {
								e.stopPropagation()
								setSelectedIndex((prev) => (prev === index ? null : index))
							}}
						>
							<div
								className="w-2.5 h-2.5 rounded-sm shrink-0 transition-opacity"
								style={{
									backgroundColor:
										entry.name === "Others" ? OTHERS_COLOR : COLORS[index % COLORS.length],
									opacity: selectedIndex === null || selectedIndex === index ? 1 : 0.35,
								}}
							/>
							<span
								className={`text-xs transition-colors ${selectedIndex === index ? "text-foreground font-medium" : "text-muted-foreground"}`}
							>
								{entry.name}
							</span>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	)
}
